import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, ChevronRight } from 'lucide-react';
import SEOHead, { buildBreadcrumb } from '@/components/SEOHead';
import SiteHeader from '@/components/SiteHeader';
import Breadcrumbs from '@/components/Breadcrumbs';

const blogPosts = [
  {
    id: 'afternoon-flight-delays-explained',
    title: "Why Your 5 PM Flight Is Always Late: The Delay Domino Effect",
    excerpt: 'Your evening aircraft has already flown five legs and inherited every delay of the day. How propagation works, and how to book against the dominoes.',
    date: 'September 14, 2026',
    readTime: '7 min read',
    category: 'Data & Insights'
  },
  {
    id: 'thanksgiving-christmas-airport-crowds',
    title: 'Thanksgiving vs. Christmas: Which Holiday Actually Wrecks Airports?',
    excerpt: 'One is a compressed 48-hour spike, the other a two-week plateau with weather risk. The crowd data says they need completely different strategies.',
    date: 'September 2, 2026',
    readTime: '8 min read',
    category: 'Travel Planning'
  },
  {
    id: 'airport-crowd-myths',
    title: '7 Airport Crowd Myths Our Data Keeps Debunking',
    excerpt: 'Red-eyes mean empty airports? Get there at 5 AM? Big airport = long lines? What hourly data from 331 airports says about the folklore.',
    date: 'August 6, 2026',
    readTime: '8 min read',
    category: 'Data & Insights'
  },
  {
    id: 'layover-connection-time-guide',
    title: 'How Much Layover Time Do You Actually Need? A Hub-by-Hub Reality Check',
    excerpt: 'Booking engines sell 35-minute connections. Whether you make them depends on the hour, the terminal map, and the delay dominoes. The realistic minimums.',
    date: 'July 14, 2026',
    readTime: '9 min read',
    category: 'Travel Planning'
  },
  {
    id: 'best-credit-cards-airport-lounges-2026',
    title: 'Are Airport Lounges Worth It? A Crowd-Data Reality Check',
    excerpt: 'We track hourly crowd levels at 331 airports. Here\'s what the data says about when lounge access actually pays off — and when an empty terminal beats it for free.',
    date: 'June 10, 2026',
    readTime: '9 min read',
    category: 'Lounges'
  },
  {
    id: 'priority-pass-complete-guide',
    title: 'Why You Keep Getting Turned Away From Airport Lounges — and How to Time It',
    excerpt: 'Lounges fill on the same departure banks that drive security lines. Use crowd data to predict "at capacity" signs before you hit them.',
    date: 'June 3, 2026',
    readTime: '8 min read',
    category: 'Lounges'
  },
  {
    id: 'travel-hacks-save-money-2026',
    title: 'The Cheapest Flights Are Also the Emptiest: 12 Data-Backed Travel Hacks for 2026',
    excerpt: 'Airfare and airport crowding are driven by the same demand curve. Twelve hacks that save money and stress at the same time, backed by our hourly data.',
    date: 'June 17, 2026',
    readTime: '9 min read',
    category: 'Travel Hacks'
  },
  {
    id: 'centurion-lounge-guide',
    title: 'Amex Centurion Lounges: Every Location Ranked and Reviewed',
    excerpt: 'A comprehensive guide to all Centurion Lounge locations, what makes each unique, and tips for avoiding the crowds.',
    date: 'March 8, 2026',
    readTime: '10 min read',
    category: 'Lounges'
  },
  {
    id: 'tsa-precheck-vs-clear-vs-global-entry',
    title: 'TSA PreCheck vs CLEAR vs Global Entry: Which Should You Get?',
    excerpt: 'A detailed comparison of trusted traveler programs to help you decide which is worth your money (and how to get them free).',
    date: 'March 5, 2026',
    readTime: '7 min read',
    category: 'Travel Tips'
  },
  {
    id: 'how-early-arrive-airport',
    title: 'How Early Should You Arrive at the Airport? The Complete Guide',
    excerpt: 'The answer depends on several factors including your destination, airport size, and time of travel. Here\'s everything you need to know to never miss a flight.',
    date: 'February 15, 2026',
    readTime: '5 min read',
    category: 'Travel Tips'
  },
  {
    id: 'busiest-travel-days-2026',
    title: 'The Busiest Travel Days of 2026: When to Avoid Flying',
    excerpt: 'Plan ahead and avoid the crowds. We break down the busiest travel days of the year and share tips for navigating peak travel periods.',
    date: 'February 12, 2026',
    readTime: '4 min read',
    category: 'Travel Planning'
  },
  {
    id: 'airport-security-tips',
    title: '10 Tips to Get Through Airport Security Faster',
    excerpt: 'From TSA PreCheck to knowing what to wear, these expert tips will help you breeze through security checkpoints.',
    date: 'February 10, 2026',
    readTime: '6 min read',
    category: 'Travel Tips'
  },
  {
    id: 'best-time-to-fly',
    title: 'The Best Time of Day to Fly: Avoiding Crowds and Delays',
    excerpt: 'Early morning or late night? We analyze flight data to reveal the optimal times to book your flights for a smoother travel experience.',
    date: 'February 8, 2026',
    readTime: '4 min read',
    category: 'Travel Planning'
  },
  {
    id: 'international-travel-checklist',
    title: 'International Travel Checklist: Don\'t Leave Home Without These',
    excerpt: 'Flying internationally requires extra preparation. Use this comprehensive checklist to ensure you have everything you need.',
    date: 'February 5, 2026',
    readTime: '7 min read',
    category: 'Travel Tips'
  }
];

export default function BlogPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <SEOHead
        title="Travel Blog — Airport Tips, Lounges & Booking Hacks"
        description="Practical airport and travel guides updated for 2026: TSA PreCheck vs CLEAR, Priority Pass lounges, busiest travel days, security shortcuts, and credit card perks."
        path="/blog"
        keywords="travel blog, airport tips, TSA PreCheck, Priority Pass, airport lounges, travel hacks, airline credit cards, busiest travel days"
        structuredData={[
          buildBreadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Blog', url: '/blog' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: 'How Busy Is My Airport Travel Blog',
            url: 'https://howbusyismyairport.com/blog',
            blogPost: blogPosts.map((p) => ({
              '@type': 'BlogPosting',
              headline: p.title,
              datePublished: p.date,
              url: `https://howbusyismyairport.com/blog/${p.id}`,
              description: p.excerpt,
            })),
          },
        ]}
      />
      {/* Header */}
      <SiteHeader />

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="pt-6 pb-2">
            <Breadcrumbs items={[{ name: 'Home', to: '/' }, { name: 'Blog' }]} />
          </div>
          
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl font-bold text-slate-800 mb-4">Travel Tips & Insights</h1>
            <p className="text-lg text-slate-600">
              Expert advice to make your airport experience smoother and stress-free.
            </p>
          </motion.div>

          {/* Blog Posts Grid */}
          <div className="space-y-6">
            {blogPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={`/blog/${post.id}`}>
                  <Card className="border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                              {post.category}
                            </span>
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.readTime}
                            </span>
                          </div>
                          <h2 className="text-xl font-semibold text-slate-800 mb-2 hover:text-blue-600 transition-colors">
                            {post.title}
                          </h2>
                          <p className="text-slate-600 text-sm mb-3">
                            {post.excerpt}
                          </p>
                          <p className="text-xs text-slate-400">{post.date}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-2" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-4">
            <Link to="/" className="text-slate-600 hover:text-blue-600 transition-colors">Home</Link>
            <Link to="/airports" className="text-slate-600 hover:text-blue-600 transition-colors">Airport Guides</Link>
            <Link to="/blog" className="text-slate-600 hover:text-blue-600 transition-colors">Blog</Link>
            <Link to="/faq" className="text-slate-600 hover:text-blue-600 transition-colors">FAQ</Link>
            <Link to="/about" className="text-slate-600 hover:text-blue-600 transition-colors">About</Link>
            <Link to="/privacy" className="text-slate-600 hover:text-blue-600 transition-colors">Privacy</Link>
            <Link to="/terms" className="text-slate-600 hover:text-blue-600 transition-colors">Terms</Link>
          </div>
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Airport Busyness Predictor. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
